// ============================================================================
// APPLY ON-TAKE-DAMAGE TRIGGERS
// ============================================================================
// [CREATED: 2026-05-17 — Spec C Task 6]
// [PURPOSE]: Wraps `effectCalculator.fireTriggeredEffects('on_take_damage', ...)`
//   and applies payload deltas to the in-flight damage amount:
//
//     - `resistance` grant      → halves amount (Math.floor) when
//                                  damageType matches; non-stacking
//                                  (RAW PHB p.197). All firings logged.
//     - any other payload type  → recorded in triggeredLogEntries so
//                                  the activity log surfaces them
//                                  (Rule 25: never silently inert).
//
//   onceThenConsume firings carry their sourceRef into pendingConsumes
//   for the caller to plumb through consumeTriggeredSource (Task 4).
//
// [WHY — INFRA ONLY in v1]: No SRD content uses on_take_damage in v1 of
//   Spec C. The helper ships so v1.x consumers (Hellish Rebuke auto-
//   retaliation, Champion Survivor regen, Stoneskin tick consumption,
//   Cloak of Fire Resistance) can wire in without re-architecting.
//   See COMING_SOON_ROADMAP §3.21 for the consumer queue.
//
//   Production HP-decrement sites (App.jsx:5670-5710 onDamage shortcut,
//   damage prompts) do NOT yet call this util — the wire-up is deferred
//   until an SRD consumer surfaces. Rule 25 is satisfied via the §3.21
//   breadcrumb + this helper being available + the test suite proving
//   it works.
//
// [SPEC]: docs/superpowers/specs/2026-05-16-triggered-effect-design.md §5/§11
// [PLAN]: docs/superpowers/plans/2026-05-16-triggered-effect.md Task 6

import { effectCalculator } from '../services/effectCalculator';

/**
 * @param {Object} playerStats
 * @param {Array}  inventory
 * @param {Object} eventContext   { amount, damageType, source? }
 * @returns {{ finalAmount: number, triggeredLogEntries: Array, pendingConsumes: Array, offeredReactions: Array }}
 *
 * [Sub-plan 1H-G-bw-S3a — 2026-06-03]: Added offeredReactions return field.
 * When damage is taken, this includes all available reactions (detected via
 * effectCalculator.getReactionTriggers). The caller (App.jsx) decides which
 * reactions to offer based on trigger type, gating, etc. For now, all
 * reactions are offered so the player can choose which to invoke.
 */
export default function applyOnTakeDamageTriggers(playerStats, inventory, eventContext) {
  const ctx = eventContext && typeof eventContext === 'object' ? eventContext : {};
  const startingAmount = Number(ctx.amount) || 0;
  const result = {
    finalAmount: startingAmount,
    triggeredLogEntries: [],
    pendingConsumes: [],
    offeredReactions: [],
  };

  // Empty / zero amount or no eventContext → nothing to halve; bail without
  // firer call (fast path; firer would return [] anyway since condition
  // checks need ctx.damageType).
  if (startingAmount <= 0) return result;

  const firings = effectCalculator.fireTriggeredEffects(
    'on_take_damage',
    playerStats,
    inventory || [],
    ctx
  );
  if (!Array.isArray(firings) || firings.length === 0) return result;

  let resistanceApplied = false;

  for (const firing of firings) {
    if (!firing || typeof firing !== 'object') continue;
    const sourceLabel = firing.sourceLabel || 'Triggered Effect';
    const payload = Array.isArray(firing.payload) ? firing.payload : [];

    for (const entry of payload) {
      if (!entry || typeof entry !== 'object') continue;

      if (entry.type === 'resistance' && entry.operation === 'grant') {
        // RAW: resistance does NOT stack with itself (PHB p.197). First
        // resistance grant halves; subsequent ones log but don't re-halve.
        const before = result.finalAmount;
        let after = before;
        if (!resistanceApplied) {
          after = Math.floor(before / 2);
          result.finalAmount = after;
          resistanceApplied = true;
        }
        result.triggeredLogEntries.push({
          sourceLabel,
          kind: 'resistance',
          damageType: entry.value || ctx.damageType || null,
          amountBefore: before,
          amountAfter: after,
        });
        continue;
      }

      // Non-resistance payload — log so the surface isn't silently inert.
      result.triggeredLogEntries.push({
        sourceLabel,
        kind: 'other',
        payloadType: entry.type,
        operation: entry.operation || null,
        value: entry.value ?? null,
      });
    }

    if (firing.shouldConsume && firing.sourceRef && typeof firing.sourceRef === 'object') {
      result.pendingConsumes.push(firing.sourceRef);
    }
  }

  // [Sub-plan 1H-G-bw-S3a — 2026-06-03]: Offer available reactions when damage is taken.
  // Get all available reactions (via effectCalculator.getReactionTriggers) and return them
  // so the caller (App.jsx) can offer them to the player. Currently, all reactions are
  // offered; future filtering by trigger type could happen here or in the caller.
  try {
    const availableReactions = effectCalculator.getReactionTriggers(playerStats, inventory || []);
    result.offeredReactions = availableReactions || [];
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[applyOnTakeDamageTriggers] getReactionTriggers threw:', e);
    result.offeredReactions = [];
  }

  return result;
}
